import * as Events from './events';

/**
 * Возвращает true, если аргумент выглядит как класс WebSocket
 */
const isWebSocket = (ws) => typeof ws !== 'undefined' && !!ws && ws.CLOSING === 2;

const DEFAULT = {
  // максимальная задержка между переподключениями, в мс
  maxReconnectionDelay: 10000,

  // минимальная задержка между переподключениями, в мс
  minReconnectionDelay: 1000 + (Math.random() * 4000),

  // min время, чтобы считать соединение стабильным, в мс
  minUptime: 5000,

  // насколько быстро растет задержка переподключения
  reconnectionDelayGrowFactor: 1.3,

  // повторите попытку подключения, если соединение не установлено по прошествии этого времени, в мс
  connectionTimeout: 4000,

  // максимальное количество попыток
  maxRetries: Infinity,

  // запустить websocket в состоянии CLOSED, вызвать `.reconnect()` для подключения
  startClosed: false,

  // включает отладочный вывод
  debug: false,
};

export default class ReconnectingWebSocket {
  _ws = null;
  _retryCount = -1;
  _uptimeTimeout = null;
  _connectTimeout = null;
  _shouldReconnect = true;
  _connectLock = false;
  _closeCalled = false;

  _url = null;
  _options = {};

  constructor(url, options = {}) {
    this._url = url;
    this._options = options;

    if (this._options.startClosed) {
      this._shouldReconnect = false;
    }

    this._connect();
  }

  static get CONNECTING() {
    return 0;
  }

  static get OPEN() {
    return 1;
  }

  static get CLOSING() {
    return 2;
  }

  static get CLOSED() {
    return 3;
  }

  get CONNECTING() {
    return ReconnectingWebSocket.CONNECTING;
  }

  get OPEN() {
    return ReconnectingWebSocket.OPEN;
  }

  get CLOSING() {
    return ReconnectingWebSocket.CLOSING;
  }

  get CLOSED() {
    return ReconnectingWebSocket.CLOSED;
  }

  /**
   * Возвращает количество попыток подключения
   */
  get retryCount() {
    return Math.max(this._retryCount, 0);
  }

  /**
   * Текущее состояние соединения; это одна из констант состояния готовности
   */
  get readyState() {
    if (this._ws) {
      return this._ws.readyState;
    }

    return this._options.startClosed
      ? ReconnectingWebSocket.CLOSED
      : ReconnectingWebSocket.CONNECTING;
  }

  /**
   * Слушатель событий, который будет вызываться,
   * когда готовое состояние соединения WebSocket изменится на CLOSED
   */
  onclose() {}

  /**
   * Слушатель событий, вызываемый при возникновении ошибки
   */
  onerror() {}

  /**
   * Слушатель событий, который будет вызываться при получении сообщения от сервера
   */
  onmessage() {}

  /**
   * Слушатель событий, который вызывается,
   * когда готовое состояние соединения WebSocket изменяется на OPEN,
   * это указывает на то, что соединение готово к отправке и получению данных
   */
  onopen() {}

  close(code = 1000, reason = 'default reason') {
    this._closeCalled = true;
    this._shouldReconnect = false;
    this._clearTimeouts();

    if (!this._ws) {
      this._debug('close enqueued: no ws instance');
      return;
    }

    if (this._ws.readyState === this.CLOSED) {
      this._debug('close: already closed');
      return;
    }

    this._ws.close(code, reason);
  }

  reconnect(code, reason) {
    this._shouldReconnect = true;
    this._closeCalled = false;
    this._retryCount = -1;

    if (!this._ws || this._ws.readyState === this.CLOSED) {
      this._connect();
    } else {
      this._disconnect(code, reason);
      this._connect();
    }
  }

  send(data) {
    if (this._ws && this._ws.readyState === this.OPEN) {
      this._debug('send', data);
      this._ws.send(data);
    }
  }

  _debug(...args) {
    if (this._options.debug) {
      // eslint-disable-next-line no-console
      console.log('RWS>', ...args);
    }
  }

  _getNextDelay() {
    const {
      reconnectionDelayGrowFactor = DEFAULT.reconnectionDelayGrowFactor,
      minReconnectionDelay = DEFAULT.minReconnectionDelay,
      maxReconnectionDelay = DEFAULT.maxReconnectionDelay,
    } = this._options;

    let delay = 0;

    if (this._retryCount > 0) {
      delay = minReconnectionDelay * Math.pow(reconnectionDelayGrowFactor, this._retryCount - 1);

      if (delay > maxReconnectionDelay) {
        delay = maxReconnectionDelay;
      }
    }

    this._debug('next delay', delay);
    return delay;
  }

  _wait() {
    return new Promise(resolve => {
      setTimeout(resolve, this._getNextDelay());
    });
  }

  _getNextUrl(urlProvider) {
    if (typeof urlProvider === 'string') {
      return Promise.resolve(urlProvider);
    }

    throw Error('Invalid URL');
  }

  _connect() {
    if (this._connectLock || !this._shouldReconnect) {
      return;
    }

    this._connectLock = true;

    const {
      maxRetries = DEFAULT.maxRetries,
      connectionTimeout = DEFAULT.connectionTimeout,
    } = this._options;

    if (this._retryCount >= maxRetries) {
      this._debug('max retries reached', this._retryCount, '>=', maxRetries);
      return;
    }

    this._retryCount++;

    this._debug('retry count connect', this._retryCount);
    this._removeListeners();

    if (!isWebSocket(WebSocket)) {
      throw Error('No valid WebSocket class provided');
    }

    this._wait()
      .then(() => this._getNextUrl(this._url))
      .then(url => {
        // close could be called before creating the ws
        if (this._closeCalled) {
          return;
        }

        this._debug('connect', { url });
        this._ws = new WebSocket(url);
        this._connectLock = false;
        this._addListeners();

        this._connectTimeout = setTimeout(() => this._handleTimeout(), connectionTimeout);
      });
  }

  _handleTimeout() {
    this._debug('timeout event');
    this._handleError(new Events.ErrorEvent(Error('TIMEOUT'), this));
  }

  _disconnect(code = 1000, reason = '') {
    this._clearTimeouts();

    if (!this._ws) {
      return;
    }

    this._removeListeners();

    try {
      this._ws.close(code, reason);
      this._handleClose(new Events.CloseEvent(code, reason, this));
    } catch (error) {
      // ignore
    }
  }

  _acceptOpen() {
    this._debug('accept open');
    this._retryCount = 0;
  }

  _handleOpen(event) {
    this._debug('[open] Connection established', event);
    const { minUptime = DEFAULT.minUptime } = this._options;

    clearTimeout(this._connectTimeout);
    this._uptimeTimeout = setTimeout(() => this._acceptOpen(), minUptime);

    if (this.onopen) {
      this.onopen(event);
    }
  }

  _handleClose(event) {
    this._clearTimeouts();

    if (event.wasClean) {
      this._debug(`[close] Connection closed cleanly, code=${event.code} reason=${event.reason}`);

      if (this._shouldReconnect) {
        this._connect();
      }

      if (this.onclose) {
        this.onclose(event);
      }
      return;
    }

    console.error('[close] Connection died');
  }

  _handleMessage(event) {
    this._debug('[message] Data received from server', event.data);

    if (this.onmessage) {
      this.onmessage(event);
    }
  }

  _handleError(event) {
    this._debug('[error]', event.message);
    this._disconnect(undefined, event.message === 'TIMEOUT' ? 'timeout' : undefined);

    if (this.onerror) {
      this.onerror(event);
    }

    this._connect();
  }

  _addListeners() {
    if (!this._ws) {
      return;
    }

    this._debug('addListeners');
    this._ws.addEventListener('open', this._handleOpen.bind(this));
    this._ws.addEventListener('close', this._handleClose.bind(this));
    this._ws.addEventListener('message', this._handleMessage.bind(this));
    this._ws.addEventListener('error', this._handleError.bind(this));
  }

  _removeListeners() {
    if (!this._ws) {
      return;
    }

    this._debug('removeListeners');
    this._ws.removeEventListener('open', this._handleOpen.bind(this));
    this._ws.removeEventListener('close', this._handleClose.bind(this));
    this._ws.removeEventListener('message', this._handleMessage.bind(this));
    this._ws.removeEventListener('error', this._handleError.bind(this));
  }

  _clearTimeouts() {
    clearTimeout(this._connectTimeout);
    clearTimeout(this._uptimeTimeout);
  }
}
